// (4) Submit flow: gated button → confirmation modal (lists unattempted) →
// POST submit → navigate to the report (with a skeleton while it generates).
import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { apiPost } from '@/lib/api';
import { attemptedCount, useExamStore } from '@/store/examStore';

export function ExamSubmitFlow() {
  const navigate = useNavigate();
  const spId = useExamStore((s) => s.spId);
  const questions = useExamStore((s) => s.questions);
  const results = useExamStore((s) => s.results);
  const attempts = useExamStore((s) => s.attempts);
  const attempted = useExamStore(attemptedCount);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const total = questions.length;
  const allAttempted = total > 0 && attempted >= total;

  const unattempted = useMemo(() => {
    return questions
      .map((q, i) => ({ q, num: i + 1 }))
      .filter(({ q }) => !results[q.id] && !(attempts[q.id]?.length));
  }, [questions, results, attempts]);

  async function confirmSubmit() {
    if (!spId) return;
    setSubmitting(true);
    try {
      await apiPost(`/student-papers/${spId}/submit`);
      // The report job may take a few seconds; the report page shows a skeleton.
      navigate(`/reports/${spId}`);
    } catch {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="sticky bottom-0 z-20 border-t border-slate-200 bg-card/95 p-3 backdrop-blur">
        <div className="mx-auto max-w-3xl">
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="w-full rounded-xl bg-slate-900 px-4 py-3 font-semibold text-white transition hover:bg-slate-800"
          >
            Submit paper
            {!allAttempted && (
              <span className="ml-2 font-normal text-slate-300">
                ({attempted}/{total} done)
              </span>
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => !submitting && setOpen(false)}
          >
            <motion.div
              className="w-full max-w-md rounded-t-2xl bg-card p-5 shadow-xl sm:rounded-2xl"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Confirm submission"
            >
              {submitting ? (
                <div className="py-6">
                  <p className="mb-3 text-center font-semibold text-ink">Submitting…</p>
                  <div className="space-y-2">
                    <div className="h-3 w-3/4 animate-pulse rounded bg-slate-200" />
                    <div className="h-3 w-full animate-pulse rounded bg-slate-200" />
                    <div className="h-3 w-5/6 animate-pulse rounded bg-slate-200" />
                  </div>
                  <p className="mt-3 text-center text-xs text-slate-400">Preparing your report…</p>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-bold text-ink">Submit your paper?</h3>
                  {unattempted.length > 0 ? (
                    <div className="mt-2">
                      <p className="text-sm text-slate-600">
                        You have <strong>{unattempted.length}</strong> unattempted question
                        {unattempted.length > 1 ? 's' : ''}:
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {unattempted.map(({ num }) => (
                          <span
                            key={num}
                            className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
                          >
                            Q{num}
                          </span>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-slate-400">
                        You can still submit — unattempted questions won't earn marks.
                      </p>
                    </div>
                  ) : (
                    <p className="mt-2 text-sm text-slate-600">
                      All questions attempted. Great work!
                    </p>
                  )}

                  <div className="mt-5 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="flex-1 rounded-xl border border-slate-300 px-4 py-2.5 font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Keep working
                    </button>
                    <button
                      type="button"
                      onClick={confirmSubmit}
                      className="flex-1 rounded-xl bg-slate-900 px-4 py-2.5 font-semibold text-white hover:bg-slate-800"
                    >
                      {unattempted.length > 0 ? 'Submit anyway' : 'Submit'}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
